import {
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  Output,
  Signal,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { PopoverController } from '@ionic/angular';
import { DatetimeChangeEventDetail, IonDatetimeCustomEvent } from '@ionic/core';
import { TranslateService } from '@ngx-translate/core';

export type DatePickerDisplayMode = 'day' | 'month' | 'year';
const LOCALES: Record<string, string> = {
  '1': 'nl-BE',
  '2': 'fr-FR',
  '3': 'en-GB',
};

@Component({
    selector: 'shared-date-picker',
    templateUrl: './date-picker.component.html',
    styleUrl: './date-picker.component.scss',
    standalone: false
})
export class DatePickerComponent {
  @Input() date = new Date();
  @Input() minDate?: Date;
  @Input() maxDate?: Date;
  private _displayMode: DatePickerDisplayMode = 'day';
  @Input() set displayMode(displayMode: DatePickerDisplayMode) {
    this._displayMode = displayMode;
    this.setPresentation(displayMode);
  }
  get displayMode(): DatePickerDisplayMode {
    return this._displayMode;
  }

  @Output() readonly dateChange = new EventEmitter<Date | null>();

  private readonly popoverController = inject(PopoverController);
  private readonly translateService = inject(TranslateService);
  private readonly uniqueId = new Date().getTime();
  private readonly langChangeEvent = toSignal(
    this.translateService.onLangChange,
  );
  readonly buttonId = `date-picker-button-${this.uniqueId}`;
  readonly presentation = signal('date');
  readonly locale = this.createLocaleFromCurrentLang(this.translateService);

  onDateChange(event: IonDatetimeCustomEvent<DatetimeChangeEventDetail>): void {
    const dateAsString = event.target.value?.toString();
    const date = dateAsString ? new Date(dateAsString) : null;
    this.popoverController.dismiss();
    this.dateChange.emit(date);
  }

  private setPresentation(displayMode: DatePickerDisplayMode): void {
    switch (displayMode) {
      case 'year':
        this.presentation.set('year');
        break;
      case 'month':
        this.presentation.set('month-year');
        break;
      default:
        this.presentation.set('date');
        break;
    }
  }

  private createLocaleFromCurrentLang(
    translateService: TranslateService,
  ): Signal<string> {
    const currentLang = translateService.currentLang;
    let [, languageId] = currentLang.split('_');
    if (!languageId) languageId = '1';
    return computed(() => {
      const langChangeEvent = this.langChangeEvent();
      if (langChangeEvent) {
        const [, changedLanguageId] = langChangeEvent.lang.split('_');
        if (changedLanguageId) languageId = changedLanguageId;
      }
      return LOCALES[languageId];
    });
  }
}
